import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { testSuitesApi } from "@/api/client";
import { queryKeys } from "@/hooks/queryKeys";
import type { TestSuite, TestSuiteCase } from "@/types/api";
import {
  removeCaseByName,
  renameCaseByName,
  suiteCaseFromTest,
  type TestAssertionDraft,
  upsertCaseByName,
  usePipelineStaging,
} from "./pipelineStaging";

// The studio's test catalog: the suite the panel stages IS the unit of
// persistence (old /studio semantics) — every op fetches that suite, edits
// its cases, and saves it back in place, written ONLY on explicit actions.
// Unstaging never touches the catalog: the staged group is session state
// like every other staged thing, while the tree lists the catalog's suites
// for re-dragging. refresh() invalidates the ["testSuites"] prefix, so
// per-suite entries and the tree follow.

type RewriteSuite = (
  suiteId: string,
  mutate: (suite: TestSuite) => Partial<TestSuite>,
) => Promise<void>;

// The suite's case-editing operations (add, batch-add, rename, remove) —
// split out from the suite-level CRUD below since every one of them is
// just a different `cases` mutation run through the shared rewriteSuite.
function useCaseMutations(rewriteSuite: RewriteSuite) {
  /** Upserts one test into its suite (untitled Save / dirty-row Save). */
  const saveCase = useCallback(
    (
      suiteId: string,
      name: string,
      rows: TestAssertionDraft[],
      strictMode = false,
    ) =>
      rewriteSuite(suiteId, (s) => ({
        cases: upsertCaseByName(
          s.cases,
          suiteCaseFromTest(name, rows, strictMode),
        ),
      })),
    [rewriteSuite],
  );

  /** Appends/updates a whole generated batch in ONE suite write —
   *  case-per-case saves would race each other's fetch. */
  const saveCases = useCallback(
    (suiteId: string, cases: TestSuiteCase[]) =>
      rewriteSuite(suiteId, (s) => ({
        cases: cases.reduce(upsertCaseByName, s.cases),
      })),
    [rewriteSuite],
  );

  const renameCase = useCallback(
    (suiteId: string, oldName: string, newName: string) =>
      rewriteSuite(suiteId, (s) => ({
        cases: renameCaseByName(s.cases, oldName, newName),
      })),
    [rewriteSuite],
  );

  /** Removes one test from its suite (a test row's minus). */
  const deleteCase = useCallback(
    (suiteId: string, name: string) =>
      rewriteSuite(suiteId, (s) => ({
        cases: removeCaseByName(s.cases, name),
      })),
    [rewriteSuite],
  );

  return { saveCase, saveCases, renameCase, deleteCase };
}

export function useSuiteCatalog() {
  const queryClient = useQueryClient();
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.testSuites }),
    [queryClient],
  );

  const rewriteSuite: RewriteSuite = useCallback(
    async (suiteId, mutate) => {
      const suite = await testSuitesApi.get(suiteId);
      if (!suite) return;
      await testSuitesApi.save({ ...suite, ...mutate(suite) });
      await refresh();
    },
    [refresh],
  );

  /** Creates an empty catalog suite (the "Add a new test suite" modal). */
  const createSuite = useCallback(
    async (name: string): Promise<TestSuite | null> => {
      const suite = await testSuitesApi.save({
        name,
        context: { type: "rhai", script: "" },
        cases: [],
      });
      await refresh();
      return suite ?? null;
    },
    [refresh],
  );

  const { saveCase, saveCases, renameCase, deleteCase } =
    useCaseMutations(rewriteSuite);

  const renameSuite = useCallback(
    (suiteId: string, name: string) => rewriteSuite(suiteId, () => ({ name })),
    [rewriteSuite],
  );

  /** Removes a whole stored suite (the tree preview's Delete). */
  const deleteSuite = useCallback(
    async (suiteId: string) => {
      await testSuitesApi.delete(suiteId);
      await refresh();
    },
    [refresh],
  );

  return {
    createSuite,
    saveCase,
    saveCases,
    renameCase,
    deleteCase,
    renameSuite,
    deleteSuite,
  };
}

/** The staged suite's catalog id, creating the stored suite on FIRST
 *  use: "Add a new test suite" stages a PENDING group with no catalog
 *  row — a suite is only saved to the tree once it has a non-empty test
 *  to hold, so the first case write goes through here. */
export function useEnsureStagedSuite() {
  const { stagedSuite, attachSuiteId } = usePipelineStaging();
  const { createSuite } = useSuiteCatalog();
  return useCallback(async (): Promise<string> => {
    if (!stagedSuite) throw new Error("No test suite staged");
    if (stagedSuite.suiteId !== null) return stagedSuite.suiteId;
    const suite = await createSuite(stagedSuite.name);
    if (!suite) throw new Error("Failed to create test suite");
    attachSuiteId(suite.id);
    return suite.id;
  }, [stagedSuite, attachSuiteId, createSuite]);
}
